import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Badge, Button, FilterChip, Input, Skeleton, Switch } from '@indipia/ui-core'
import { ErrorBanner } from './AsyncState'
import {
  getForemanConfirmSetting,
  patchForemanConfirmSetting,
  resetForemanConfirmSetting,
  type ForemanConfirmSetting,
  type ForemanConfirmSettingPatch,
} from '../api/foremanApi'

// 공사알림 설정 — 시공파트너 일정확인 알림 요청 섹션 (기획서 F-1 ②).
// 저장 버튼 없이 변경 즉시 PATCH. 낙관적 갱신 → 실패 시 마지막 서버 확정값으로 복귀.
// 연속 변경: 변경 필드를 모아(마지막 값 우선) 한 번에 하나씩만 전송하는 순차 큐.
// 다음 반장 자동 발송은 켜고 끄기만 한다 — 순위는 2순위 1차례로 고정(서버 AUTO_NEXT_MAX_CNT=2, 순위 선택 삭제 2026-10-30).

const DUE_CHIP_HOURS = [6, 12, 24, 48]
const DUE_MIN = 1
const DUE_MAX = 72
const DUE_DEFAULT = 24

/** 1~72 로 맞춤(숫자가 아니면 24) */
function clampDue(raw: number): number {
  if (!Number.isFinite(raw)) return DUE_DEFAULT
  return Math.min(DUE_MAX, Math.max(DUE_MIN, Math.round(raw)))
}

const SAVED_VISIBLE_MS = 2000

const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '15px 18px' } as const
const labelStyle = { fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' } as const
const descStyle = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginTop: 2 } as const
const hintStyle = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', marginTop: 2 } as const

export function ForemanConfirmSettingSection() {
  const [setting, setSetting] = useState<ForemanConfirmSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false) // 전송 중(초기화 버튼 잠금용)
  const [dueDraft, setDueDraft] = useState('') // 숫자 입력 편집 중 문자열

  const confirmedRef = useRef<ForemanConfirmSetting | null>(null) // 서버가 확정한 마지막 값
  const pendingRef = useRef<ForemanConfirmSettingPatch>({})
  const inflightRef = useRef(false)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    getForemanConfirmSetting()
      .then(s => { confirmedRef.current = s; setSetting(s); setDueDraft(String(s.replyDuration)) })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [reloadKey])

  const flashSaved = useCallback(() => {
    setSaved(true)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    savedTimerRef.current = setTimeout(() => setSaved(false), SAVED_VISIBLE_MS)
  }, [])

  const flush = useCallback(async () => {
    if (inflightRef.current) return
    inflightRef.current = true
    setBusy(true)
    try {
      while (Object.keys(pendingRef.current).length > 0) {
        const patch = pendingRef.current
        pendingRef.current = {}
        try {
          const res = await patchForemanConfirmSetting(patch)
          confirmedRef.current = { ...(confirmedRef.current as ForemanConfirmSetting), ...patch, isDefault: res?.isDefault ?? false }
          if (aliveRef.current) flashSaved()
        } catch {
          // 이 요청과 이후 대기분을 버리고 서버 확정값으로 복귀
          pendingRef.current = {}
          if (aliveRef.current) {
            setSetting(confirmedRef.current)
            if (confirmedRef.current) setDueDraft(String(confirmedRef.current.replyDuration))
            setSaved(false)
          }
          toast.error('저장하지 못했어요. 다시 시도해 주세요')
        }
      }
    } finally {
      inflightRef.current = false
      if (aliveRef.current) setBusy(false)
    }
  }, [flashSaved])

  const change = (patch: ForemanConfirmSettingPatch) => {
    setSetting(prev => (prev ? { ...prev, ...patch } : prev))
    pendingRef.current = { ...pendingRef.current, ...patch }
    void flush()
  }

  const handleReset = async () => {
    if (inflightRef.current) return
    inflightRef.current = true
    setBusy(true)
    pendingRef.current = {}
    try {
      const res = await resetForemanConfirmSetting()
      confirmedRef.current = res
      setSetting(res)
      setDueDraft(String(res.replyDuration))
      flashSaved()
      toast.success('기본값으로 되돌렸어요')
    } catch {
      toast.error('초기화하지 못했어요. 다시 시도해 주세요')
    } finally {
      inflightRef.current = false
      setBusy(false)
    }
  }

  // 답장 기한 — 칩·입력 모두 시간 단위로만 저장
  const changeDue = (hours: number) => {
    setDueDraft(String(hours))
    if (setting && setting.replyDuration === hours && setting.durationUnit === 'HOUR') return
    change({ replyDuration: hours, durationUnit: 'HOUR' })
  }
  const commitDueDraft = () => changeDue(clampDue(Number(dueDraft)))

  return (
    <section style={{ marginBottom: 20 }} aria-label="시공파트너 일정확인 알림 요청">
      <div style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 16, overflow: 'hidden' }}>
        {/* 섹션 머리 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '15px 18px', borderBottom: '1px solid var(--shell-border)' }}>
          <span
            aria-hidden="true"
            style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--brand-kakao-container)', color: 'var(--brand-kakao-symbol)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}
          >톡</span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)' }}>시공파트너 일정확인 알림 요청</div>
            <div style={descStyle}>반장님께 보내는 일정확인 알림 요청의 답장 기한과 자동 발송 규칙이에요</div>
          </div>
          <Badge variant="soft" status="mute">받는 사람 : 시공파트너</Badge>
        </div>

        {loading ? (
          <div aria-busy="true" style={{ padding: '15px 18px', display: 'grid', gap: 14 }}>
            <Skeleton height={44} />
            <Skeleton height={44} />
          </div>
        ) : loadError || !setting ? (
          <div style={{ padding: 18 }}>
            <ErrorBanner message="시공파트너 일정확인 설정을 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
          </div>
        ) : (
          <>
            {/* 답장 기한 — 좌 설명 · 우 입력(칩 위, 숫자 입력 아래, 오른쪽 정렬) */}
            <div style={{ ...rowStyle, flexWrap: 'nowrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={labelStyle}>답장 기한</div>
                <div style={descStyle}>일정확인 알림 요청을 보낸 뒤 설정한 시간이 지나도 답이 없으면<br />&apos;미응답&apos;으로 봐요</div>
                <div style={descStyle}>예) 오후 3시 발송 + 24시간 → 다음날 오후 3시까지 미응답이면 다음 반장님께</div>
                <div style={hintStyle}>착공 하루 전 07:00(아침 7시)이 더 빠르면 그때까지만 기다려요</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                <div role="group" aria-label="빠른 선택" style={{ display: 'flex', gap: 6 }}>
                  {DUE_CHIP_HOURS.map(h => {
                    const on = setting.durationUnit === 'HOUR' && setting.replyDuration === h
                    return (
                      <FilterChip key={h} size="sm" active={on} aria-pressed={on} onClick={() => changeDue(h)}>{h}시간</FilterChip>
                    )
                  })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Input
                    type="number"
                    size="sm"
                    width="auto"
                    min={DUE_MIN}
                    max={DUE_MAX}
                    step={1}
                    inputMode="numeric"
                    aria-label="답장 기한(시간)"
                    value={dueDraft}
                    onChange={e => setDueDraft(e.target.value)}
                    onBlur={commitDueDraft}
                    onKeyDown={e => { if (e.key === 'Enter') commitDueDraft() }}
                  />
                  <span style={{ ...descStyle, marginTop: 0 }}>시간 후 미응답</span>
                </div>
              </div>
            </div>

            {/* 다음 반장님 자동 발송 */}
            <div style={{ ...rowStyle, flexWrap: 'nowrap', borderTop: '1px solid var(--shell-border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={labelStyle}>다음 반장님 자동 발송</div>
                <div style={descStyle}>&apos;불가&apos; 답장은 바로, 답장 기한까지 답이 없으면 2순위 반장님께 한 번 자동으로 일정확인 알림 요청을 보내요</div>
                <div style={hintStyle}>&apos;불가&apos; 답장 · 일정이 겹치는 반장님은 제외 · 2순위 반장님도 확정되지 않으면 할일로 알려 드려요</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  aria-label="다음 반장님 자동 발송"
                  checked={setting.autoNext}
                  onChange={e => change({ autoNext: e.target.checked })}
                />
              </div>
            </div>
          </>
        )}

        {/* 푸터 */}
        <div style={{ ...rowStyle, borderTop: '1px solid var(--shell-border)', padding: '12px 18px' }}>
          <span
            role="status"
            aria-live="polite"
            style={{ fontSize: 'var(--type-caption-size)', color: saved ? 'var(--shell-point)' : 'var(--shell-hint)', fontWeight: saved ? 700 : 400 }}
          >
            {saved ? '✓ 저장됨' : '변경하면 바로 저장돼요'}
          </span>
          <Button variant="etc" size="sm" onClick={() => { void handleReset() }} disabled={busy || loading || loadError || !setting}>초기화</Button>
        </div>
      </div>
    </section>
  )
}
